// src/pages/Website/Alma_Mater/AlmaMater.js
import React, { useEffect, useState } from 'react';
import { postAPI } from '../../../app/api'; // Your API helper
import Header from '../../Universal/Header';
import Footer from '../../Universal/Footer';
import galleryBanner from "../../../theme/assets/images/gallery-banner.svg";
import viewIcon from "../../../assets/img/view-icon.svg";
import Modal from '../../../GlobalComponent/Modal';

const { REACT_APP_BRANDURL, REACT_APP_APIURL } = process.env;

// Skeleton loader animation
const skeletonStyles = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = skeletonStyles;
  document.head.appendChild(style);
}

const AlmaMater = () => {
  const [clickedImg, setClickedImg] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [heroData, setHeroData] = useState(null);
  const [heroLoading, setHeroLoading] = useState(true);
  const [almaImages, setAlmaImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);

  const fallbackHeroData = {
    title: "Our Alma Mater",
    description: "A tribute to our roots at B E College—where shared memories, enduring values, and lifelong bonds unite generations of alumni.",
    image: galleryBanner,
    route: "alma-mater"
  };

  // Fetch hero and gallery images
  useEffect(() => {
    fetchHeroData();
    fetchAlmaImages();
  }, []);

  const fetchHeroData = async () => {
    try {
      setHeroLoading(true);
      const response = await postAPI('hero-data/fetchData/almamater', {});
      setHeroData(response?.status === 1 ? response.data : fallbackHeroData);
    } catch (error) {
      console.error('Error fetching hero data:', error);
      setHeroData(fallbackHeroData);
    } finally {
      setHeroLoading(false);
    }
  };

  const fetchAlmaImages = async () => {
    try {
      setImagesLoading(true);
      console.log('Fetching AlmaMater images...');
      const response = await postAPI('gallery/listAlmaMaterFiles', {});
      console.log('AlmaMater API response:', response);
      if (response?.status === 1 && response?.data) {
        const formattedImages = response.data.map(item => ({
          ...item,
          link: item.link?.startsWith('http') ? item.link : `${REACT_APP_BRANDURL}${item.link}`
        }));
        console.log('Formatted AlmaMater images:', formattedImages);
        setAlmaImages(formattedImages);
      } else {
        console.log('AlmaMater API returned no data or error status:', response);
      }
    } catch (error) {
      console.error('Error fetching Alma Mater images:', error);
    } finally {
      setImagesLoading(false);
    }
  };

  const handleClick = (item, index) => {
    setCurrentIndex(index);
    setClickedImg(item.link);
  };

  const handleRotationRight = () => {
    if (!almaImages.length) return;
    const nextIndex = currentIndex + 1 >= almaImages.length ? 0 : currentIndex + 1;
    setCurrentIndex(nextIndex);
    setClickedImg(almaImages[nextIndex].link);
  };

  const handleRotationLeft = () => {
    if (!almaImages.length) return;
    const prevIndex = currentIndex === 0 ? almaImages.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setClickedImg(almaImages[prevIndex].link);
  };

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="banner-image">
          {heroLoading ? (
            <div style={{ height: '500px', backgroundColor: '#e0e0e0', animation: 'pulse 1.5s infinite', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#999' }}>Loading...</span>
            </div>
          ) : (
            <img
              src={heroData?.backgroundImage ? (heroData.backgroundImage.startsWith('http') ? heroData.backgroundImage : `${REACT_APP_APIURL}${heroData.backgroundImage}`) : fallbackHeroData.image}
              alt="banner"
              className="img-fluid"
              style={{ width: '100%', height: '500px', objectFit: 'cover' }}
              onError={(e) => { e.target.src = fallbackHeroData.image; }}
            />
          )}
        </div>
        <div className="hero-content">
          {heroLoading ? (
            <>
              <div className="skeleton-loader" style={{ height: '60px', backgroundColor: '#e0e0e0', marginBottom: '20px', animation: 'pulse 1.5s infinite', borderRadius: '4px' }} />
              <div className="skeleton-loader" style={{ height: '80px', backgroundColor: '#e0e0e0', animation: 'pulse 1.5s infinite', borderRadius: '4px' }} />
            </>
          ) : (
            <>
              <h1 className="fw-bold display-5">{heroData?.title || fallbackHeroData.title}</h1>
              <p className="mt-lg-3 mt-1 mb-0">{heroData?.description || fallbackHeroData.description}</p>
            </>
          )}
        </div>
      </section>

      {/* Alma Mater Gallery */}
      <div className="infrastructure">
        <div className="container">
          <div className="row justify-content-center">
            {imagesLoading ? (
              [...Array(6)].map((_, idx) => (
                <div key={idx} className="col-lg-4 col-md-6 col-sm-10 col-12">
                  <div className="gallery-item" style={{ height: '250px', backgroundColor: '#e0e0e0', animation: 'pulse 1.5s infinite', borderRadius: '8px', marginBottom: '20px' }} />
                </div>
              ))
            ) : almaImages.length ? (
              almaImages.map((item, idx) => (
                <div key={idx} className="col-lg-4 col-md-6 col-sm-10 col-12">
                  <div className="gallery-item">
                    <img src={item.link} alt={item.title || "Alumni"} />
                    <div className="overlay" onClick={() => handleClick(item, idx)}>
                      <span className="plus-icon"><img src={viewIcon} alt="view" /></span>
                      <div className="img-info">
                        <h6 className="mb-0">{item.title}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5">
                <p className="text-muted">No alumni images available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal */}
      {clickedImg && (
        <Modal
          clickedImg={clickedImg}
          handelRotationRight={handleRotationRight}
          setClickedImg={setClickedImg}
          handelRotationLeft={handleRotationLeft}
        />
      )}

      <Footer />
    </>
  );
};

export default AlmaMater;
